import { useState, useEffect } from 'react'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where
} from 'firebase/firestore'
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Stack,
  CardHeader,
  Button,
  TableSortLabel,
  Tooltip,
  IconButton,
  ButtonGroup
} from '@mui/material'
import { BarChart, PieChart } from '@mui/x-charts'
import DownloadButton from '../../Document/DownloadButton'
import { useNavigate } from 'react-router-dom'
import { logEvent } from 'firebase/analytics'
import Loading from '../../Helpers/Loading'
import noData from '../../../Icons/noData.jpg'
import logo from '../../../Icons/logoAlt.png'
import '../../../Style/PulseReport.css'
import {
  BarChartOutlined,
  FirstPage,
  PieChartOutline,
  Print
} from '@mui/icons-material'

const PulseReport = ({ db, uid, analytics }) => {
  const [document, setDocument] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sectionData, setSectionData] = useState([])
  const [showPie, setShowPie] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    logEvent(analytics, 'pulse_report')
  }, [])

  const navigateBack = () => {
    const pathname = window.location.pathname
    const result = pathname.substring(pathname.lastIndexOf('/') + 1)
    navigate('/viewPulse/' + result)
  }

  useEffect(() => {
    const fetchDocument = async () => {
      const pathname = window.location.pathname
      const result = pathname.substring(pathname.lastIndexOf('/') + 1)
      try {
        const docRef = doc(db, 'sliders', result)
        const docSnap = await getDoc(docRef)

        if (docSnap.exists()) {
          let docu = docSnap.data()
          if (
            !(
              docu &&
              (docu.uid === uid || (docu.admins && docu.admins.includes(uid)))
            )
          ) {
            navigate('/viewPulse/' + result)
          }
          setDocument({ ...docSnap.data(), id: docSnap.id })
        } else {
          const q = query(
            collection(db, 'sliders'),
            where('customRoute', '==', result)
          )
          const querySnapshot = await getDocs(q)
          if (!querySnapshot.empty) {
            const firstDoc = querySnapshot.docs[0]
            let docu = firstDoc.data()
            if (
              !(
                docu &&
                (docu.uid === uid || (docu.admins && docu.admins.includes(uid)))
              )
            ) {
              navigate('/viewPulse/' + result)
            }
            setDocument({ ...firstDoc.data(), id: firstDoc.id })
          }
        }
      } catch (error) {
        console.error('Error fetching document:', error)
        navigate('/viewPulse/' + result)
      } finally {
        setLoading(false)
      }
    }
    if (uid) fetchDocument()
  }, [db, uid])

  useEffect(() => {
    if (!document?.pulseSections) return

    const processed = document.pulseSections.map((section, sectionIndex) => {
      const commentsArray = (document.comments || []).filter(
        c => c.sectionIndex === sectionIndex
      )

      let type = 'Slider'
      if (section.isABTesting) type = 'AB Testing'
      else if (section.isMultipleChoice) type = 'Multiple Choice Grid'
      else if (section.isChoice) type = 'Multiple Choice'

      let chartData = []
      let gridData = []
      let sliderPrompts = []

      // --- AB TESTING & CHOICE ---
      if (section.isABTesting || section.isChoice) {
        const options = section.isABTesting
          ? section.pulseOptions
          : section.pulseChoice
        chartData = (options || []).map((opt, idx) => {
          const label = section.isABTesting ? opt.text : opt
          return {
            id: idx,
            label: label.replace(/<[^>]*>/g, ''),
            value: section.isABTesting
              ? commentsArray.filter(c => c.option === label).length
              : commentsArray.reduce(
                  (acc, curr) => (curr.option?.[idx]?.value ? acc + 1 : acc),
                  0
                )
          }
        })
      }
      // --- GRID ---
      else if (section.isMultipleChoice) {
        gridData = (section.pulseGrid?.rows || []).map((row, rowIndex) => {
          const rowName = row.replace(/<[^>]*>/g, '')
          const data = section.pulseGrid.columns.map((col, colIdx) => {
            const value = commentsArray.reduce(
              (acc, curr) =>
                curr.option?.[rowIndex]?.label === row &&
                curr.option?.[rowIndex]?.value === col
                  ? acc + 1
                  : acc,
              0
            )
            return {
              id: colIdx,
              value: value,
              label: col
            }
          })
          return { rowName, data }
        })
      }
      // --- SLIDER ---
      else {
        sliderPrompts = (section.pulsePrompts || []).map((prompt, pIdx) => {
          const promptComments = (document.comments || []).filter(
            c => c.sectionIndex === sectionIndex && c.pulseIndex === pIdx
          )
          return {
            prompt,
            chartData: (document.pulseValues || []).map(val => ({
              label: val.toString(),
              value: promptComments.filter(c => c.option === val).length
            }))
          }
        })
      }

      return {
        title: section.title,
        type,
        chartData, // For Pie/Bar
        gridData, // For Stacked Bar
        sliderPrompts, // For multi-sliders
        columns: section.pulseGrid?.columns || [],
        totalVotes: commentsArray.length
      }
    })

    setSectionData(processed)
  }, [document])

  const printReport = () => {
    window.print()
  }

  if (loading)
    return (
      <Card
        raised
        sx={{
          margin: { md: 2, xs: 1 },
          marginTop: { xs: 2 }
        }}
      >
        <Loading margin='auto' padding='100px 0' />
      </Card>
    )
  if (!document)
    return (
      <Card
        raised
        sx={{
          margin: { md: 2, xs: 1 },
          marginTop: { xs: 2 }
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <img
            src={noData}
            alt='No Pulse'
            style={{ borderRadius: '4px', width: '100%' }}
          />
          <Typography variant='h3' sx={{ fontWeight: 'bold' }}>
            No Pulse Found
          </Typography>
          <Button variant='contained' onClick={navigateBack}>
            Back
          </Button>
        </CardContent>
      </Card>
    )

  const TableElement = ({ idx, pIdx }) => {
    const [order, setOrder] = useState('desc')
    const [orderBy, setOrderBy] = useState('dateString')

    const handleRequestSort = property => {
      const isAsc = orderBy === property && order === 'asc'
      setOrder(isAsc ? 'desc' : 'asc')
      setOrderBy(property)
    }

    const filteredComments =
      document.comments
        ?.filter(
          c =>
            c.sectionIndex === idx && (pIdx === null || c.pulseIndex === pIdx)
        )
        .filter(c => c.message) || []

    const sortedComments = [...filteredComments].sort((a, b) => {
      let aValue = a[orderBy] || ''
      let bValue = b[orderBy] || ''

      if (orderBy === 'dateString') {
        aValue = new Date(a.dateString).getTime()
        bValue = new Date(b.dateString).getTime()
      }

      if (bValue < aValue) return order === 'asc' ? 1 : -1
      if (bValue > aValue) return order === 'asc' ? -1 : 1
      return 0
    })

    return (
      <TableContainer
        component={Paper}
        variant='outlined'
        sx={{ borderRadius: 3 }}
        className={sortedComments.length > 0 ? '' : 'hide-for-print'}
      >
        <Table>
          <TableHead sx={{ bgcolor: '#F3F6F9' }}>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'displayName'}
                  direction={orderBy === 'displayName' ? order : 'asc'}
                  onClick={() => handleRequestSort('displayName')}
                >
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell>Comment</TableCell>
              <TableCell align='right'>
                <TableSortLabel
                  active={orderBy === 'dateString'}
                  direction={orderBy === 'dateString' ? order : 'asc'}
                  onClick={() => handleRequestSort('dateString')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedComments.length > 0 ? (
              sortedComments.map((comment, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {comment.displayName || 'Anonymous'}
                  </TableCell>
                  <TableCell>
                    {comment.message || (
                      <Typography variant='caption' color='textSecondary'>
                        No text comment provided
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align='right'>{comment.dateString}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align='center' sx={{ py: 3 }}>
                  <Typography color='textSecondary'>
                    {'No comments found for this ' +
                      (pIdx === null ? 'section.' : 'prompt.')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <Card
      id='print-area'
      raised
      sx={{
        margin: { md: 2, xs: 1 },
        marginTop: { xs: 2 }
      }}
    >
      <CardHeader
        title={document.fileName}
        action={
          <span className={'hide-for-print'}>
            <Tooltip title={'Back to Pulse'}>
              <IconButton onClick={navigateBack}>
                <FirstPage />
              </IconButton>
            </Tooltip>
            <DownloadButton document={document} icon db={db} isPulse />
            <Tooltip title={'Print Report'}>
              <IconButton onClick={printReport}>
                <Print />
              </IconButton>
            </Tooltip>
          </span>
        }
      />
      <CardContent>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid>
            <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant='overline'>Total Responses</Typography>
              <Typography variant='h4'>
                {document.comments?.length || 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid>
            <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant='overline'>Total Comments</Typography>
              <Typography variant='h4'>
                {document.comments?.filter(c => c.message).length || 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid>
            <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant='overline'>Total Viewers</Typography>
              <Typography variant='h4'>{document.visitedBy.length || 0}</Typography>
            </Paper>
          </Grid>
        </Grid>
        <ButtonGroup
          variant='outlined'
          className={'hide-for-print'}
          sx={{ pb: 2, width: '100%', justifyContent: 'center' }}
        >
          <Button
            onClick={() => setShowPie(false)}
            startIcon={<BarChartOutlined />}
            variant={showPie ? 'outlined' : 'contained'}
          >
            Bar Charts
          </Button>
          <Button
            onClick={() => setShowPie(true)}
            startIcon={<PieChartOutline />}
            variant={showPie ? 'contained' : 'outlined'}
          >
            Pie Charts
          </Button>
        </ButtonGroup>
        {sectionData.map((section, idx) => (
          <Card
            key={idx + '_section' + section.title}
            variant='outlined'
            sx={{ mb: 2 }}
          >
            <CardContent>
              <Stack
                direction='row'
                spacing={2}
                alignItems='center'
                sx={{ mb: 4 }}
              >
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                  {section.title}
                </Typography>
                <Chip label={section.type} size='small' color='primary' />
              </Stack>

              {section.totalVotes > 0 ? (
                <Box>
                  {section.type === 'Multiple Choice Grid' ? (
                    <>
                      {!showPie ? (
                        <Stack spacing={2} sx={{ mb: 2 }}>
                          {section.gridData.map((row, idx2) => (
                            <Box key={idx + 'sectionGridData' + idx2}>
                              <Typography
                                variant='subtitle2'
                                color='textSecondary'
                              >
                                {row.rowName}
                              </Typography>
                              <Box height={350}>
                                <BarChart
                                  dataset={row.data}
                                  xAxis={[
                                    { scaleType: 'band', dataKey: 'label' }
                                  ]}
                                  yAxis={[{ tickMinStep: 1 }]}
                                  series={[
                                    {
                                      dataKey: 'value'
                                    }
                                  ]}
                                  slotProps={{
                                    legend: {
                                      hidden: true
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Stack spacing={2} sx={{ mb: 2 }}>
                          {section.gridData.map((row, idx2) => (
                            <Box key={idx + 'sectionGridData' + idx2}>
                              <Typography
                                variant='subtitle2'
                                color='textSecondary'
                              >
                                {row.rowName}
                              </Typography>
                              <PieChart
                                series={[
                                  {
                                    data: row.data,
                                    innerRadius: 40
                                  }
                                ]}
                                height={350}
                                slotProps={{ legend: { hidden: true } }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </>
                  ) : section.type === 'Slider' ? (
                    section.sliderPrompts.map((s, pIdx) => (
                      <div key={pIdx + '_sectionReport' + s.prompt}>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant='subtitle2' color='textSecondary'>
                            {s.prompt}
                          </Typography>
                          <Stack sx={{ mb: 2 }}>
                            {!showPie ? (
                              <Box sx={{ height: 350 }}>
                                <BarChart
                                  dataset={s.chartData}
                                  xAxis={[
                                    { scaleType: 'band', dataKey: 'label' }
                                  ]}
                                  yAxis={[{ tickMinStep: 1 }]}
                                  series={[
                                    { dataKey: 'value', color: '#4caf50' }
                                  ]}
                                />
                              </Box>
                            ) : (
                              <Box width={'100%'} height={350}>
                                <PieChart
                                  series={[
                                    { data: s.chartData, innerRadius: 40 }
                                  ]}
                                />
                              </Box>
                            )}
                          </Stack>
                        </Box>
                        <TableElement idx={idx} pIdx={pIdx} />
                      </div>
                    ))
                  ) : section.type === 'Multiple Choice' ? (
                    <Stack sx={{ mb: 2 }} className={'mcView'}>
                      {section.chartData.map((data, idxD) => (
                        <div
                          key={
                            data.label +
                            '_mcData' +
                            idxD +
                            '_idx' +
                            idx +
                            section.title
                          }
                        >
                          {!showPie ? (
                            <Box width={'100%'} height={350}>
                              <BarChart
                                xAxis={[
                                  {
                                    scaleType: 'band',
                                    data: [
                                      data.label || 'Selected',
                                      'Did Not Select'
                                    ]
                                  }
                                ]}
                                series={[
                                  {
                                    data: [
                                      data.value ? data.value : 0,
                                      section.totalVotes - (data.value || 0)
                                    ]
                                  }
                                ]}
                                slotProps={{
                                  legend: {
                                    direction: 'horizontal',
                                    position: {
                                      vertical: 'bottom',
                                      horizontal: 'center'
                                    }
                                  }
                                }}
                                height={300}
                              />
                            </Box>
                          ) : (
                            <Box width={'100%'}>
                              <PieChart
                                series={[
                                  {
                                    data: [
                                      {
                                        ...data,
                                        value: data.value ? data.value : 0
                                      },
                                      {
                                        label: 'Did Not Select',
                                        value: section.totalVotes - data.value,
                                        color: 'gray'
                                      }
                                    ],
                                    innerRadius: 20
                                  }
                                ]}
                                slotProps={{
                                  legend: {
                                    direction: 'horizontal',
                                    position: {
                                      vertical: 'bottom',
                                      horizontal: 'center'
                                    }
                                  }
                                }}
                              />
                            </Box>
                          )}
                        </div>
                      ))}
                    </Stack>
                  ) : (
                    <Stack sx={{ mb: 2 }}>
                      {!showPie ? (
                        <Box width={'100%'} height={350}>
                          <BarChart
                            dataset={section.chartData}
                            xAxis={[{ scaleType: 'band', dataKey: 'label' }]}
                            yAxis={[{ tickMinStep: 1 }]}
                            series={[{ dataKey: 'value', color: '#007FFF' }]}
                          />
                        </Box>
                      ) : (
                        <Box width={'100%'} height={350}>
                          <PieChart
                            series={[
                              { data: section.chartData, innerRadius: 40 }
                            ]}
                          />
                        </Box>
                      )}
                    </Stack>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: 'center',
                    mb: 2
                  }}
                >
                  <Typography color='textSecondary'>
                    No responses yet for this section.
                  </Typography>
                </Box>
              )}

              {section.type !== 'Slider' && (
                <TableElement idx={idx} pIdx={null} />
              )}
            </CardContent>
          </Card>
        ))}{' '}
        <img
          className='watermark'
          alt='logo'
          src={logo}
          style={{
            maxWidth: '100%',
            objectFit: 'contain',
            height: '60px',
            cursor: 'pointer',
            borderRadius: '4px',
            margin: '8px',
            float: 'right'
          }}
        />
      </CardContent>
    </Card>
  )
}

export default PulseReport
